/**
 * @typedef {import("@prismicio/client").Content.FaqAccordionSlice} FaqAccordionSlice
 * @typedef {import("@prismicio/react").SliceComponentProps<FaqAccordionSlice>} FaqAccordionProps
 * @type {import("react").FC<FaqAccordionProps>}
 */
"use client";
import * as prismic from "@prismicio/client";
import { PrismicRichText } from "@/components/PrismicRichText";

import { useState } from "react";
import { usePathname } from "next/navigation";

const FaqAccordion = ({ slice }) => {
  const [openIndex, setOpenIndex] = useState(null);
  const handleToggle = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  const pathname = usePathname();
  const baseUrl =
    process.env.NEXT_PUBLIC_SITE_URL || "https://proearclinic.co.uk";
  const normalizedBaseUrl = baseUrl.replace(/\/$/, "");
  const normalizedPath = pathname?.startsWith("/")
    ? pathname
    : `/${pathname ?? ""}`;
  const pageUrl = `${normalizedBaseUrl}${normalizedPath}`;
  const faqUrl = `${pageUrl}#faq`;

  const faqEntities = slice.primary.items
    .map((item) => {
      const question = prismic.asText(item.question).trim();
      const answer = prismic.asText(item.answer).trim();

      if (!question || !answer) {
        return null;
      }

      return {
        "@type": "Question",
        name: question,
        acceptedAnswer: {
          "@type": "Answer",
          text: answer,
        },
      };
    })
    .filter(Boolean);

  const faqSchema =
    faqEntities.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": faqUrl,
          url: pageUrl,
          mainEntity: faqEntities,
        }
      : null;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      id="faq"
    >
      <section className="section-faq tf-spacing-1">
        <div className="tf-container">
          <div className="row justify-center">
            <div
              className={`${slice.primary.full_width ? "w-full" : "col-md-8"}`}
            >
              {faqSchema ? (
                <script
                  type="application/ld+json"
                  dangerouslySetInnerHTML={{
                    __html: JSON.stringify(faqSchema),
                  }}
                />
              ) : null}
              <div className="heading-section ">
                <p className="text-2 sub">{slice.primary.eyebrow}</p>
                <h2>Questions and Answers</h2>
                <p className="description text-1 lh-30">
                  {prismic.asText(slice.primary.description)}
                </p>
              </div>
              <div className="tf-accordion" id="accordion">
                {slice.primary.items.map((item, index) => (
                  <div className="tf-accordion-item border-b" key={index}>
                    <button
                      className={`w-full flex justify-between items-center py-4 text-left focus:outline-none transition-colors ${openIndex === index ? "text-blue-700" : "text-slate-900"}`}
                      onClick={() => handleToggle(index)}
                      aria-expanded={openIndex === index}
                      aria-controls={`faq-content-${index}`}
                    >
                      <span className="font-medium">
                        <h3 className="text-4xl">
                          {prismic.asText(item.question)}
                        </h3>
                      </span>
                      <span className="ml-2 flex items-center">
                        {openIndex === index ? (
                          // Minus icon
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-12 w-12"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M5 12h14"
                            />
                          </svg>
                        ) : (
                          // Plus icon
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-12 w-12"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M12 5v14m-7-7h14"
                            />
                          </svg>
                        )}
                      </span>
                    </button>
                    <div
                      id={`faq-content-${index}`}
                      className={`overflow-hidden transition-all duration-300 ${openIndex === index ? " opacity-100 py-2" : "max-h-0 opacity-0 py-0"}`}
                    >
                      <div className="tf-accordion-body">
                        <PrismicRichText field={item.answer} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
    </section>
  );
};

export default FaqAccordion;
